import { create } from 'zustand';
import { useIncomingCallStore } from './incoming-call.store';
import { registerUserScopedReset } from './session-read';

/**
 * What THIS device did with a ring (P7d), so the sheet and the push agree
 * on the one ring they both are (same id, `ring_id` in the push's data):
 *
 *  - handled: answered here (Not now, Escape) or taken over by a push tap.
 *    The sheet does not show it again even if a poll still lists it.
 *  - accepted: Accept won here. A later tap on the same ring's push opens
 *    the live call instead of the page that would offer a second accept.
 *  - failed: a "Not now" that never reached the server (offline) gives the
 *    ring back with this message, instead of hiding a ring the server still
 *    thinks is unanswered (review #991, Codex).
 *
 * `handled` is kept in sessionStorage (review #991, S10) so a reload does
 * not show a ring this tab already answered; a per-tab convenience only
 * (the server's own state is what decides), so every read and write is
 * guarded and the store works without it.
 */
interface RingState {
  handled: Record<number, true>;
  accepted: Record<number, true>;
  failed: Record<number, string>;
  markHandled: (id: number) => void;
  markAccepted: (id: number) => void;
  /** The decline did not reach the server: show the ring again, with why. */
  restore: (id: number, message: string) => void;
  reset: () => void;
}

const HANDLED_KEY = 'empathease.ring.handled';
/** Ring ids are server-wide and short-lived: the newest few are enough. */
const HANDLED_KEEP = 50;

function loadHandled(): Record<number, true> {
  try {
    const raw = sessionStorage.getItem(HANDLED_KEY);
    const ids: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(ids)) return {};
    return Object.fromEntries(
      ids.filter((id): id is number => Number.isInteger(id) && id > 0).map((id) => [id, true]),
    ) as Record<number, true>;
  } catch {
    return {};
  }
}

function saveHandled(handled: Record<number, true>): void {
  try {
    const ids = Object.keys(handled).map(Number).slice(-HANDLED_KEEP);
    if (ids.length === 0) sessionStorage.removeItem(HANDLED_KEY);
    else sessionStorage.setItem(HANDLED_KEY, JSON.stringify(ids));
  } catch {
    // Storage blocked or full: the in-memory set still works for this load.
  }
}

export const useRingStore = create<RingState>((set) => ({
  handled: loadHandled(),
  accepted: {},
  failed: {},
  markHandled: (id) =>
    set((s) => {
      const handled: Record<number, true> = { ...s.handled, [id]: true };
      saveHandled(handled);
      return { handled };
    }),
  markAccepted: (id) =>
    set((s) => {
      const handled: Record<number, true> = { ...s.handled, [id]: true };
      saveHandled(handled);
      return { accepted: { ...s.accepted, [id]: true }, handled };
    }),
  restore: (id, message) =>
    set((s) => {
      const { [id]: _gone, ...handled } = s.handled;
      saveHandled(handled);
      return { handled, failed: { ...s.failed, [id]: message } };
    }),
  reset: () => {
    saveHandled({});
    set({ handled: {}, accepted: {}, failed: {} });
  },
}));

/** Where a call lives once it has started: the 1:1 tab opens it. */
export const LIVE_CALL_ROUTE = '/app/empathy?tab=1on1';

/**
 * A push was tapped: ONE function for both kinds of call push (class D,
 * RING-PUSH spec 5), so the listener's ring and the requester's incoming call
 * never diverge on what "already answered here" means.
 *
 *  - a listener ring (`ring_id`): if this device already accepted that ring,
 *    go to the live call; otherwise the push's own route takes over and the
 *    sheet for that ring closes.
 *  - an incoming call (`empathy_request_claimed` with `session_id`): if this
 *    device already answered that call (joined, Not now, or it ran out
 *    here), the 1:1 page, with nothing to ring and no action to run; else
 *    the route as resolved (the 1:1 page with the sheet for that session).
 *
 * Anything else: the route as resolved.
 */
export function routeForPushTap(
  data: Record<string, string | undefined>,
  resolved: string,
): string {
  const ringId = Number(data.ring_id);
  if (Number.isInteger(ringId) && ringId > 0) {
    const state = useRingStore.getState();
    if (state.accepted[ringId]) return LIVE_CALL_ROUTE;
    state.markHandled(ringId);
    return resolved;
  }
  if (data.type === 'empathy_request_claimed') {
    const sessionId = Number(data.session_id);
    if (
      Number.isInteger(sessionId) &&
      sessionId > 0 &&
      useIncomingCallStore.getState().handled[sessionId]
    ) {
      return LIVE_CALL_ROUTE;
    }
  }
  return resolved;
}

registerUserScopedReset(() => useRingStore.getState().reset());
